import { MarginProps, styledMargin } from '@tink/core/src/components/atoms/Margins/margins';
import { widths, WidthsProps } from '@tink/core/src/components/atoms/Width/widths';
import { Palette, PaletteVariants } from 'styled-components';
import { css, styled, TinkLinkFontSizes } from '../../../core/theme/styled';
import { TinkLinkTheme } from '../../ApplicationProviders/ConfiguredThemeProvider/tinkLinkTheme';

type MutualStylesProps = MarginProps &
  WidthsProps & {
    size?: TinkLinkFontSizes;
    color?: Palette;
    shade?: PaletteVariants;
    bold?: boolean;
    align?: 'left' | 'center' | 'right';
    truncate?: boolean;
    monospace?: boolean;
  };

export const getFontSize = ({
  size,
  theme,
}: {
  size?: TinkLinkFontSizes;
  theme: TinkLinkTheme;
}) => {
  if (!size) {
    return theme.typography.paragraph.fontSize;
  }

  if (size === 'inherit') {
    return 'inherit';
  }

  return theme.sizes[size];
};

export const getColor = ({
  color,
  shade,
  theme,
}: {
  color?: Palette;
  shade?: PaletteVariants;
  theme: TinkLinkTheme;
}) => {
  if (!color) {
    return theme.configuration.primaryTextColor;
  }

  // both are configurable colors, not from Palette, but have the same naming.
  if (color === 'secondary') {
    return theme.configuration.secondaryTextColor;
  }

  if (color === 'error') {
    return theme.configuration.errorTextColor;
  }

  return theme.palette[color][shade || 'main'];
};

export const mutualStyles = ({ align, bold, truncate, monospace }: MutualStylesProps) => {
  return css<MutualStylesProps>`
    color: ${(props) => getColor(props)};

    font-weight: ${(props) =>
      bold ? props.theme.font.weights.semiBold : props.theme.font.weights.regular};

    font-family: ${(props) => (monospace ? props.theme.font.monospace : props.theme.font.face)};

    ${widths};
    ${styledMargin}

    ${align &&
    `
    text-align: ${align};
    `}

    ${truncate &&
    `
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      `}
  `;
};

export const Text = styled.p<MutualStylesProps>`
  ${(...props) => mutualStyles(...props)}

  font-size: ${(props) => getFontSize(props)};
  line-height: ${(props) => props.theme.typography.paragraph.lineHeight};
`;

export const Caption = styled.p<MutualStylesProps>`
  ${(...props) => mutualStyles(...props)}

  font-size: ${(props) => props.theme.typography.caption.fontSize};
  line-height: ${(props) => props.theme.typography.caption.lineHeight};
`;
